import { Add } from "@mui/icons-material";
import { Box, Button, Card, Grid, styled, useTheme } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Typography } from "@mui/material";
import { toast } from "react-hot-toast";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MembersDataTable from "page-sections/Members/members_table";
import AddMemberModal from "page-sections/Members/AddMemberModal";
import MembersBusinessDoneCard from "page-sections/Members/BDCard";
import MembersChapterLongCard from "page-sections/Members/LCard";
import BadgeIcon from "@mui/icons-material/Badge";
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ShareIcon from '@mui/icons-material/Share';
import { usePageTitle } from "contexts/PageTitleContext";

const Members = () => {
  const { setPageTitle } = usePageTitle();
  useEffect(() => {
    setPageTitle('Member Management');
}, [setPageTitle]);
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const loggedUser = JSON.parse(localStorage.getItem("logged_data") || "{}")?.user;
  
  // Get chapterId and chapterName based on user role
  const { chapterId, chapterName } = loggedUser?.role === "chapter_admin" 
    ? {
        chapterId: loggedUser.chapter_id,
        chapterName: loggedUser.chapter_name
      }
    : location.state || {};
  
  const [members, setMembers] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());
  const [searchValue, setSearchValue] = useState("");

  const ButtonWrapper = styled(FlexBox)(({ theme }) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column",
    },
    "& .MuiButton-root": {
      minWidth: "100%",
    },
  },
}))

const DatePickerWrapper = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    "& .react-datepicker-wrapper": {
      width: "130px"
    },
    "& .react-datepicker__input-container input": {
      padding: "8px 12px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      width: "100%",
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      transition: theme.transitions.create(['border-color', 'box-shadow']),
      '&:hover': {
        borderColor: theme.palette.primary.main
      },
      '&:focus': {
        borderColor: theme.palette.primary.main,
        outline: 'none',
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}25`
      }
    },
    "& .react-datepicker": {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontFamily: theme.typography.fontFamily,
      "& .react-datepicker__header": {
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "& .react-datepicker__current-month": {
          color: theme.palette.text.primary,
          fontSize: "0.875rem",
          fontWeight: theme.typography.fontWeightMedium
        },
        "& .react-datepicker__day-name": {
          color: theme.palette.text.secondary
        }
      },
      "& .react-datepicker__day": {
        color: theme.palette.text.primary,
        "&:hover": {
          backgroundColor: theme.palette.action.hover
        },
        "&.react-datepicker__day--selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark
          }
        },
        "&.react-datepicker__day--keyboard-selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText
        },
        "&.react-datepicker__day--disabled": {
          color: theme.palette.text.disabled
        }
      },
      "& .react-datepicker__triangle": {
        display: "none"
      }
    }
  }));

  const SummaryText = styled(Typography)(({ theme }) => ({
    margin: '24px 0',
    padding: '16px 24px',
    background: `linear-gradient(to right, ${theme.palette.primary.light}15, ${theme.palette.background.paper})`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '4px',
    color: theme.palette.text.primary,
    fontSize: '1rem',
    fontWeight: 400,
    display: 'block',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
      transform: 'translateY(-1px)',
    },
    '& .date': {
      color: theme.palette.primary.main,
      fontWeight: 600,
      position: 'relative',
      '&:after': {
        content: '""',
        position: 'absolute',
        bottom: -2,
        left: 0,
        width: '100%',
        height: '2px',
        background: `${theme.palette.primary.main}25`,
      }
    }
  }));

  const fetchMembers = async () => {
    if (!chapterId) {
      // toast.error("No chapter selected");
      return;
    }
    
    setIsLoading(true);
    try {
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];
      
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_member_summery}?chapter_id=${chapterId}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      );
      
      if (response?.success) {
        setMembers(response.data.members || []);
        setSummaryData(response);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
      // toast.error("Failed to fetch members");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [chapterId, startDate, endDate]);

  const filteredMembers = members.filter(member => 
    member.member_name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const list1 = summaryData?.totals ? [
    {
      id: 1,
      amount: summaryData.totals.total_referrals,
      Icon: ShareIcon,
      title: "Total Referrals",
      color: theme.palette.primary.main
    },
    {
      id: 2,
      amount: summaryData.totals.total_one_to_ones,
      Icon: HandshakeIcon,
      title: "Total One to Ones",
      color: theme.palette.info.main
    },
    {
      id: 3,
      amount: summaryData.totals.total_members,
      Icon: GroupsIcon,
      title: "Total Members",
      color: theme.palette.text.disabled
    }
  ] : [];

  return (
    <Box pt={2} pb={4}>
      <Grid container spacing={3}>
        <Grid item md={4} xs={12}>
          <MembersBusinessDoneCard businessDone={summaryData?.totals?.total_business_done || 0} />
        </Grid>

        <Grid item md={8} xs={12}>
          <MembersChapterLongCard list={list1} />
        </Grid>
      </Grid>

      

      <Grid item xs={12}>
        <Typography variant="h5" mb={2}>
          Members in {chapterName}
        </Typography>

        <FlexBetween flexWrap="wrap">
          <FlexBox alignItems="center" gap={3}>
            <SearchInput 
              bordered={false} 
              placeholder="Find Members" 
              onChange={(e) => setSearchValue(e.target.value)} 
            />
            
            
            <DatePickerWrapper>
              <DatePicker
                selected={startDate}
                onChange={date => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
                placeholderText="Start Date"
                dateFormat="MM/dd/yyyy"
              />
              <DatePicker
                selected={endDate}
                onChange={date => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                placeholderText="End Date"
                dateFormat="MM/dd/yyyy"
              />
            </DatePickerWrapper>
          </FlexBox>


          <ButtonWrapper alignItems="center">
            <Button 
              variant="contained" 
              size="small" 
              endIcon={<Add />} 
              onClick={() => setOpenModal(true)}
            >
              {t("Add Member")}
            </Button>
          </ButtonWrapper>
        </FlexBetween>

        <AddMemberModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSuccess={fetchMembers}
          chapterId={chapterId}
          fetchMembers={fetchMembers}
        />
        <SummaryText>
        Summary of Members from{' '}
        <span className="date">
          {startDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </span>{' '}
        to{' '}
        <span className="date">
          {endDate.toLocaleDateString('en-US', { 
            month: 'long', 
            day: 'numeric', 
            year: 'numeric' 
          })}
        </span>
      </SummaryText>

        <Card sx={{ marginTop: 3 }}>
          <MembersDataTable
            data={filteredMembers}
            isLoading={isLoading}
            onRefresh={fetchMembers}
            chapterId={chapterId}
          />
        </Card>
      </Grid>
    </Box>
  );
};

export default Members;